import { z } from 'zod';

import { ExternalReachStateSchema } from './external-reach-state';
import { NodeOutputSchema } from './node-output';
import { AgentPlanSchema, flattenPlanNodes } from './plan';

/**
 * Whole-turn lifecycle status. Distinct from `NodeOutputStatus`
 * (`completed | streaming` — per-output, persisted) and
 * `PlanNodeLifecycleStatus` (`started | awaitingInput | completed | failed |
 * cancelled` — per-node, persisted). These vocabularies model different
 * cardinalities; this enum tracks the turn, not a node.
 */
export const PLAN_STATE_STATUSES = {
  done: 'done',
  executing: 'executing',
  failed: 'failed',
  planning: 'planning',
} as const;
export const PlanStateStatusSchema = z.enum(PLAN_STATE_STATUSES);
export type PlanStateStatus = z.infer<typeof PlanStateStatusSchema>;

export const PlanStateBudgetsSchema = z.object({
  tokensUsed: z.number().int().nonnegative(),
});
export type PlanStateBudgets = z.infer<typeof PlanStateBudgetsSchema>;

export const PLAN_NODE_LIFECYCLE_STATUSES = {
  /**
   * Non-terminal: the node paused on a tool that needs the user (an
   * `askUser` question or a `needsApproval` gate) and the turn suspended.
   * Distinct from `started` (actively streaming) and from `completed`: the
   * node is not done — it's waiting on the user — so the UI keeps it
   * in-progress rather than flashing a green check while a card sits
   * unanswered. A resume re-runs the node (back through `started`) and it
   * finally lands `completed`.
   */
  awaitingInput: 'awaitingInput',
  /**
   * Terminal: the turn was cancelled (user hit Stop) while this node was
   * still running. Stamped by `finalizeActivity` when it force-flips a
   * node left `'started'` at a cancelled finalize. Distinct from `failed`
   * so the UI renders a neutral "Cancelled" affordance rather than a red
   * error — a user-initiated Stop is not a node failure.
   */
  cancelled: 'cancelled',
  completed: 'completed',
  failed: 'failed',
  started: 'started',
} as const;
export const PlanNodeLifecycleStatusSchema = z.enum(PLAN_NODE_LIFECYCLE_STATUSES);
export type PlanNodeLifecycleStatus = z.infer<typeof PlanNodeLifecycleStatusSchema>;

/**
 * A node lifecycle status is terminal once the node can no longer transition:
 * it finished (`completed`), broke (`failed`), or was stopped (`cancelled`).
 * `started` and `awaitingInput` are non-terminal — the node is still running or
 * deliberately paused. Callers use this to keep node state monotonic: a
 * progress write (a late streaming snapshot, an activity retry) must never
 * downgrade a terminal node back to `started`.
 */
export function getIsTerminalNodeState(status: PlanNodeLifecycleStatus): boolean {
  return (
    status === PLAN_NODE_LIFECYCLE_STATUSES.cancelled ||
    status === PLAN_NODE_LIFECYCLE_STATUSES.completed ||
    status === PLAN_NODE_LIFECYCLE_STATUSES.failed
  );
}

export const PlanNodeLifecycleStateSchema = z.object({
  completedAt: z.string().nullable(),
  error: z.string().nullable(),
  startedAt: z.string().nullable(),
  status: PlanNodeLifecycleStatusSchema,
});
export type PlanNodeLifecycleState = z.infer<typeof PlanNodeLifecycleStateSchema>;

/**
 * The task and/or workflow the thread is bound to. Rendered into every
 * subagent's user message as `<activeContext>` so a node can resolve
 * contextual mentions such as `[@Intake Form](mention:intake-form/current)`
 * to a real workflow id without asking the user which workflow is meant.
 */
export const ActiveContextSchema = z.object({
  task: z
    .object({
      id: z.uuid(),
      parentWorkflowId: z.uuid(),
      parentWorkflowName: z.string(),
      title: z.string(),
    })
    .optional(),
  workflow: z.object({ id: z.uuid(), name: z.string() }).optional(),
});
export type ActiveContext = z.infer<typeof ActiveContextSchema>;

/**
 * Whether a person is watching the turn. Interactive turns may pause for tool
 * approval; background turns (such as an Aline AI task kickoff) never pause.
 */
export const CHAT_RUN_MODES = { background: 'background', interactive: 'interactive' } as const;
export type ChatRunMode = (typeof CHAT_RUN_MODES)[keyof typeof CHAT_RUN_MODES];

/**
 * Server-derived context captured when the plan was built, persisted so a
 * continuation (resume after a tool approval / askUser answer) can re-run
 * nodes with the same document/playbook/user context as the first pass
 * instead of empty arrays. The id arrays are broader than current-turn
 * mentions: they include prior-thread artifacts and documents belonging to
 * in-context workflows, all access-filtered (what the server code calls
 * `available*Ids`).
 */
export const PlanTurnContextSchema = z.object({
  /** The active task/workflow the first pass ran under, replayed on resume. */
  activeContext: ActiveContextSchema.optional(),
  /** May carry the active task id driving this turn (task-delegated chats). */
  activeTaskId: z.uuid().optional(),
  documentIds: z.array(z.uuid()),
  /** The document open in the editor when the turn started, if any. */
  editorDocumentId: z.uuid().nullable(),
  /**
   * Server-verified facts materialized from this turn's external (Composio)
   * results — imported document ids keyed by vendor artifact, and the
   * observed/selected message evidence recipients were resolved from. Absent
   * for turns that never reached externally, and for turns persisted before
   * external reach existed.
   */
  externalReach: ExternalReachStateSchema.optional(),
  playbookIds: z.array(z.uuid()),
  /** Absent for turns persisted before run modes existed; those resume as interactive. */
  runMode: z.enum(CHAT_RUN_MODES).optional(),
  userIds: z.array(z.uuid()),
});
export type PlanTurnContext = z.infer<typeof PlanTurnContextSchema>;

const BasePlanStateSchema = z.object({
  budgets: PlanStateBudgetsSchema,
  nodeOutputs: z.record(z.string(), NodeOutputSchema),
  /**
   * Per-node execution metadata, keyed by stringified plan node id to match
   * `nodeOutputs`. Node body text and token usage stay in `nodeOutputs`.
   */
  nodeStates: z.record(z.string(), PlanNodeLifecycleStateSchema).default({}),
  /**
   * Serialized Braintrust parent-span reference (`Span.export()` from
   * the `braintrust` package). Set by routeAndPlanActivity once and
   * re-attached by every subsequent executeNodeActivity via
   * `traced(cb, { parent })` so the entire chat turn appears as a
   * single Braintrust trace tree. Optional: old planStates persisted
   * before this field was added still parse, and readers fall back to
   * per-activity nesting (a separate root trace per activity) when
   * absent.
   *
   * TODO(review.md#34): move into a `tracing: { parentSpanExport? }`
   * sub-namespace so a future tracing-shape change doesn't bump the
   * top-level PlanState schema. Needs a migration that lifts the field
   * for already-persisted plans; not done in this pass.
   */
  parentSpanExport: z.string().optional(),
  /**
   * Planner-authored markdown shown before node outputs. It may contain
   * a `text-thinking` fence, visible prose, or both.
   */
  plannerText: z.string(),
  /**
   * Informational hint only — see the matching comment on
   * `AgentPlanSchema.schemaVersion` in `./plan.ts`. Persisted plans
   * with older values still parse; the structural validators below are
   * what gate validity.
   */
  schemaVersion: z.string(),
  /**
   * Optional: planStates persisted before this field existed still parse.
   * Written once by routeAndPlanActivity alongside `status: 'executing'`;
   * read by prepareResumeContextActivity when a suspended turn resumes.
   */
  turnContext: PlanTurnContextSchema.optional(),
});

/**
 * Cross-field invariant guard applied after the discriminated-union
 * parse. `status` and `nodeOutputs` evolve independently in storage, so
 * the parser can't fully prevent contradictory rows — but obviously
 * incoherent shapes (e.g. `status='done'` with the map key disagreeing
 * with the contained `nodeOutput.nodeId`) get surfaced loudly here so a
 * silent drift between the row and the cache becomes a parse error.
 */
function addPlanStateInvariantIssues(
  planState: z.infer<typeof BasePlanStateSchema> & {
    status: PlanStateStatus;
    plan?: z.infer<typeof AgentPlanSchema>;
  },
  ctx: z.RefinementCtx
): void {
  for (const [key, output] of Object.entries(planState.nodeOutputs)) {
    if (key !== String(output.nodeId)) {
      ctx.addIssue({
        code: 'custom',
        message: `nodeOutputs key "${key}" disagrees with nodeOutput.nodeId ${output.nodeId}; the record key must match the nested id.`,
        path: ['nodeOutputs', key, 'nodeId'],
      });
    }
  }

  if (planState.status === PLAN_STATE_STATUSES.done) {
    if (!planState.plan) {
      ctx.addIssue({
        code: 'custom',
        message: 'PlanState marked done but has no plan attached.',
        path: ['plan'],
      });
      return;
    }
    const planNodeIds = flattenPlanNodes(planState.plan).map(({ node }) => node.id);
    const missing = planNodeIds.filter(id => {
      const output = planState.nodeOutputs[String(id)];
      return !output || output.status !== 'completed';
    });
    if (missing.length > 0) {
      ctx.addIssue({
        code: 'custom',
        message: `PlanState marked done but ${missing.length} node(s) have no completed output: ${missing.join(', ')}. The leak-detection guard in finalize.activity should have caught this — verify the finalize order.`,
        path: ['status'],
      });
    }
  }
}

export const PlanStateSchema = z
  .discriminatedUnion('status', [
    BasePlanStateSchema.extend({
      plan: AgentPlanSchema.optional(),
      status: z.enum([PLAN_STATE_STATUSES.planning, PLAN_STATE_STATUSES.failed]),
    }),
    BasePlanStateSchema.extend({
      plan: AgentPlanSchema,
      status: z.enum([PLAN_STATE_STATUSES.executing, PLAN_STATE_STATUSES.done]),
    }),
  ])
  .superRefine(addPlanStateInvariantIssues);
export type PlanState = z.infer<typeof PlanStateSchema>;
